import React, { useState, useMemo } from 'react'
import { C, Avatar, Spinner, Empty, Mono, FormTrend } from '../components/ui'
import PlayerDrawer from '../components/PlayerDrawer'
import { useFetch } from '../hooks/useFetch'
import { getLeaderboard, todayStr } from '../api/client'

const MARKETS = [
  { key: 'HRR_over_1.5', label: 'HRR', desc: 'H+R+RBI Over 1.5' },
  { key: 'total_bases',  label: 'TB',  desc: 'Total Bases Over 1.5' },
  { key: 'HR_anytime',   label: 'HR',  desc: 'Home Run Anytime' },
  { key: 'strikeouts',   label: 'K',   desc: 'Strikeouts Over 4.5' },
]

const TIER_FILTERS = ['All', '🐳 Whales', '✅ Strong', '🟡 Lean']

function getTier(prob) {
  if (prob >= 0.65) return 'whale'
  if (prob >= 0.52) return 'strong'
  if (prob >= 0.40) return 'lean'
  return 'skip'
}

function getTierColor(tier) {
  if (tier === 'whale')  return C.green
  if (tier === 'strong') return C.blue
  if (tier === 'lean')   return C.yellow
  return C.dim
}

function getTierLabel(tier) {
  if (tier === 'whale')  return '🐳 Whale'
  if (tier === 'strong') return '✅ Strong'
  if (tier === 'lean')   return '🟡 Lean'
  return '⚪ Skip'
}

function exportCSV(entries, market) {
  const headers = ['Rank','Player','Team','Pitcher','Hit%','Fair Odds','Line','Book','Edge%','Tier','Park','SP Adj','Venue']
  const rows = entries.map((e, i) => {
    const prob = parseFloat(e.prob_over || 0)
    const tier = getTier(prob)
    return [
      i + 1,
      `"${e.full_name || ''}"`,
      e.team || '',
      `"${e.opp_pitcher_name || ''}"`,
      (prob * 100).toFixed(1) + '%',
      e.implied_odds_over || '',
      e.line != null ? 'O' + e.line : '',
      e.book_over != null ? (e.book_over > 0 ? '+' : '') + e.book_over : '',
      e.edge_pct != null ? (e.edge_pct * 100).toFixed(1) + '%' : '',
      getTierLabel(tier),
      e.park_factor ? parseFloat(e.park_factor).toFixed(2) : '',
      e.pitcher_adj ? parseFloat(e.pitcher_adj).toFixed(2) : '',
      `"${e.venue || ''}"`,
    ].join(',')
  })
  const csv = [headers.join(','), ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `linebrkr_${market}_${todayStr()}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function SortHeader({ label, field, sort, onSort, align = 'right' }) {
  const active = sort.field === field
  return (
    <div onClick={() => onSort(field)} style={{
      fontSize: 9, color: active ? C.blue : C.dim,
      fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
      letterSpacing: '.08em', textTransform: 'uppercase',
      cursor: 'pointer', textAlign: align,
      display: 'flex', alignItems: 'center', gap: 3,
      justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
    }}>
      {label}
      {active && <span style={{ color: C.blue }}>{sort.dir === 'desc' ? '↓' : '↑'}</span>}
    </div>
  )
}

function LeaderboardRow({ player, rank, idx, onClick }) {
  const prob = parseFloat(player.prob_over || 0)
  const tier = getTier(prob)
  const color = getTierColor(tier)
  const edge = player.edge_pct != null ? (player.edge_pct * 100).toFixed(1) : null
  const hasEdge = edge != null && parseFloat(edge) > 0.5
  const line = player.line != null ? player.line : null

  return (
    <div onClick={onClick} style={{
      display: 'grid',
      gridTemplateColumns: '28px 36px 1fr 44px 36px 50px 54px',
      alignItems: 'center', gap: 8,
      padding: '10px 14px', borderBottom: `1px solid ${C.dim2}`,
      cursor: 'pointer',
    }}>
      <Mono size={10} color={C.dim}>{String(rank).padStart(2,'0')}</Mono>
      <Avatar name={player.full_name || ''} idx={idx} size={32} />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {player.full_name}
          </div>
          <FormTrend trend={player.form_trend} delta={player.form_delta_pct} />
        </div>
        <div style={{ display: 'flex', gap: 5, marginTop: 2, alignItems: 'center' }}>
          <span style={{
            background: color + '20', border: `1px solid ${color}44`,
            borderRadius: 4, padding: '1px 5px', fontSize: 8.5, fontWeight: 700,
            fontFamily: "'Rajdhani', sans-serif", color, letterSpacing: '.04em',
          }}>
            {getTierLabel(tier)}
          </span>
          <Mono size={8.5} color={C.dim}>{player.team} · {(player.opp_pitcher_name||'TBD').substring(0,12)}</Mono>
        </div>
      </div>
      {/* HIT % */}
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 16, color, lineHeight: 1 }}>
          {(prob * 100).toFixed(0)}%
        </div>
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, color: C.dim, marginTop: 1 }}>
          {player.implied_odds_over || '—'}
        </div>
      </div>
      {/* LINE */}
      <div style={{ textAlign: 'right' }}>
        {line != null ? (
          <Mono size={10} color={C.dim}>O{line}</Mono>
        ) : (
          <Mono size={10} color={C.dim}>—</Mono>
        )}
      </div>
      {/* BOOK */}
      <div style={{ textAlign: 'right' }}>
        {player.book_over != null ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Mono size={12} color={C.blue} bold>{player.book_over > 0 ? '+' : ''}{player.book_over}</Mono>
            {player.book_under != null && (
              <Mono size={9} color={C.dim}>{player.book_under > 0 ? '+' : ''}{player.book_under}</Mono>
            )}
            {player.book_used && (
              <Mono size={8} color={C.dim}>{player.book_used.replace('williamhill_us','wh').replace('betonlineag','bol').replace('pointsbetus','pbu')}</Mono>
            )}
          </div>
        ) : (
          <Mono size={10} color={C.dim}>—</Mono>
        )}
      </div>
      {/* EDGE */}
      <div style={{ textAlign: 'right' }}>
        {hasEdge ? (
          <div style={{
            background: `${C.green}18`, border: `1px solid ${C.green}30`,
            borderRadius: 5, padding: '2px 6px', display: 'inline-block',
          }}>
            <Mono size={10} color={C.green}>+{edge}%</Mono>
            {player.warning_flags?.includes('no_vig_unavailable') && (
              <Mono size={7} color={C.yellow}>~vig</Mono>
            )}
          </div>
        ) : (
          <Mono size={10} color={C.dim}>—</Mono>
        )}
      </div>
    </div>
  )
}

export default function SharpCoreTab() {
  const [market, setMarket] = useState('HRR_over_1.5')
  const [tierFilter, setTierFilter] = useState('All')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({ field: 'prob_over', dir: 'desc' })
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const today = todayStr()

  const { data, loading, error } = useFetch(
    () => getLeaderboard(market, today, 200),
    [market, today]
  )

  const handleSort = (field) => {
    setSort(s => ({ field, dir: s.field === field && s.dir === 'desc' ? 'asc' : 'desc' }))
  }

  const entries = useMemo(() => {
    let list = data?.entries || []
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(e =>
        (e.full_name||'').toLowerCase().includes(q) ||
        (e.team||'').toLowerCase().includes(q) ||
        (e.opp_pitcher_name||'').toLowerCase().includes(q)
      )
    }
    if (tierFilter === '🐳 Whales') list = list.filter(e => parseFloat(e.prob_over||0) >= 0.65)
    else if (tierFilter === '✅ Strong') list = list.filter(e => { const p = parseFloat(e.prob_over||0); return p >= 0.52 && p < 0.65 })
    else if (tierFilter === '🟡 Lean') list = list.filter(e => { const p = parseFloat(e.prob_over||0); return p >= 0.40 && p < 0.52 })
    return [...list].sort((a, b) => {
      if (sort.field === 'full_name') {
        const av = (a.full_name||'').toLowerCase(), bv = (b.full_name||'').toLowerCase()
        return sort.dir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv)
      }
      const av = parseFloat(a[sort.field]||0), bv = parseFloat(b[sort.field]||0)
      return sort.dir === 'desc' ? bv - av : av - bv
    })
  }, [data, search, tierFilter, sort])

  const allEntries = data?.entries || []
  const whaleCount = allEntries.filter(e => parseFloat(e.prob_over||0) >= 0.65).length
  const edgeCount  = allEntries.filter(e => (e.edge_pct||0) > 0.05).length
  const marketInfo = MARKETS.find(m => m.key === market)

  return (
    <div style={{ paddingBottom: 84 }}>
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: `linear-gradient(180deg, ${C.bk} 90%, transparent)`,
        padding: '16px 16px 0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: C.blue, fontSize: 18 }}>◆</span>
              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 20 }}>Sharp Core</span>
            </div>
            {marketInfo && <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>{marketInfo.desc}</div>}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setShowFilters(f => !f)} style={{
              background: showFilters ? C.blueBg : 'none',
              border: `1px solid ${showFilters ? C.blueBdr : C.border}`,
              borderRadius: 6, padding: '5px 10px', color: showFilters ? C.blue : C.dim,
              fontSize: 10, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
              letterSpacing: '.08em', cursor: 'pointer',
            }}>FILTER</button>
            <button onClick={() => exportCSV(entries, market)} style={{
              background: C.blueBg, border: `1px solid ${C.blueBdr}`,
              borderRadius: 6, padding: '5px 10px', color: C.blue,
              fontSize: 10, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
              letterSpacing: '.08em', cursor: 'pointer',
            }}>↓ CSV</button>
          </div>
        </div>

        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}` }}>
          {MARKETS.map(m => (
            <button key={m.key} onClick={() => setMarket(m.key)} style={{
              flex: 1, background: 'none', border: 'none', padding: '8px 0 10px',
              fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 13,
              letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer',
              color: market === m.key ? C.blue : C.dim,
              borderBottom: `2px solid ${market === m.key ? C.blue : 'transparent'}`,
              transition: 'all .18s',
            }}>{m.label}</button>
          ))}
        </div>

        <div style={{ padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search player, team, pitcher..." style={{ width: '100%', background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', color: C.text, fontSize: 12, fontFamily: "'Outfit', sans-serif", boxSizing: 'border-box' }} />
        </div>
        {showFilters && (
          <div style={{ padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
            <div />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {TIER_FILTERS.map(t => (
                <button key={t} onClick={() => setTierFilter(t)} style={{
                  background: tierFilter === t ? C.blueBg : 'none',
                  border: `1px solid ${tierFilter === t ? C.blueBdr : C.border}`,
                  borderRadius: 6, padding: '4px 10px', color: tierFilter === t ? C.blue : C.dim,
                  fontSize: 10, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, cursor: 'pointer',
                }}>{t}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Stats strip */}
      <div style={{ padding: '10px 14px', display: 'flex', gap: 8 }}>
        {[
          { l: 'Showing', v: entries.length, c: C.text },
          { l: '🐳 Whales', v: whaleCount, c: C.green },
          { l: 'Edge plays', v: edgeCount, c: C.blue },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1, background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 8px', textAlign: 'center' }}>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 18, color: s.c }}>{s.v}</div>
            <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginTop: 1 }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Column headers */}
      <div style={{
        display: 'grid', gridTemplateColumns: '28px 36px 1fr 44px 36px 50px 54px',
        gap: 8, padding: '6px 14px', borderBottom: `1px solid ${C.border}`,
      }}>
        <div /><div />
        <SortHeader label="Player" field="full_name" sort={sort} onSort={handleSort} align="left" />
        <SortHeader label="Hit %" field="prob_over" sort={sort} onSort={handleSort} />
        <SortHeader label="Line" field="line" sort={sort} onSort={handleSort} />
        <SortHeader label="Book" field="book_over" sort={sort} onSort={handleSort} />
        <SortHeader label="Edge" field="edge_pct" sort={sort} onSort={handleSort} />
      </div>

      {loading && <Spinner />}
      {error && <Empty message="Could not load data" />}
      {!loading && !error && entries.length === 0 && <Empty message={search ? "No players match search" : "No plays for today"} />}

      {!loading && !error && entries.map((player, i) => (
        <LeaderboardRow key={player.player_id || i} player={player} rank={i+1} idx={i} onClick={() => setSelectedPlayer(player)} />
      ))}

      {!loading && entries.length > 0 && (
        <div style={{ padding: '16px 14px', textAlign: 'center' }}>
          <button onClick={() => exportCSV(entries, market)} style={{
            background: C.blueBg, border: `1px solid ${C.blueBdr}`,
            borderRadius: 10, padding: '10px 24px', color: C.blue,
            fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 13,
            letterSpacing: '.08em', cursor: 'pointer',
          }}>
            ↓ EXPORT {entries.length} PLAYS AS CSV
          </button>
          <div style={{ fontSize: 9, color: C.dim, marginTop: 6 }}>
            {marketInfo?.desc} · {today} · tap any row for player intel
          </div>
        </div>
      )}

      {selectedPlayer && (
        <PlayerDrawer player={selectedPlayer} onClose={() => setSelectedPlayer(null)} />
      )}
    </div>
  )
}
